/**
 * Share Draft Preview — Types
 */

/** Decoded base64 `report` query parameter from the OTP email link */
export interface OtpReportPayload {
  listingKey: string;
  ownerNumber: 'Owner1' | 'Owner2';
  email: string;
  phone?: string;
  agentKey?: string;
  tenant?: string;
}

/** POST /api/share-draft-otp-send */
export interface OtpSendRequest {
  listingKey: string;
  owner: 'Owner1' | 'Owner2';
  destinationType: 'email' | 'phone';
  email: string;
  phone?: string;
}

export interface OtpSendResponse {
  success: boolean;
  message: string;
  attemptsRemaining?: number;
  error?: string;
  code?: string;
}

/** POST /api/share-draft-otp-verify */
export interface OtpVerifyRequest {
  listingKey: string;
  owner: 'Owner1' | 'Owner2';
  email: string;
  destination: string;
  token: string;
}

export interface OtpVerifyResponse {
  success: boolean;
  previewToken?: string;
  attemptsRemaining?: number;
  error?: string;
  code?: string;
}

/** GET /api/get-listing-preview */
export interface ListingPreviewResponse {
  listing: {
    Property?: Record<string, any>;
    Media?: any[];
    OpenHouse?: any[];
    [key: string]: any;
  };
  previewMeta?: {
    ownerNumber: string;
    email: string;
    expiresAt: number;
  };
}

/** POST /api/share-draft-set-view */
export interface SetViewRequest {
  listingKey: string;
  token: string;
}

export interface SetViewResponse {
  success: boolean;
}

/** OTP flow stage state machine */
export type OtpStage =
  | 'delivery_select'
  | 'code_entry'
  | 'verifying'
  | 'success'
  | 'error';

/** Known OTP error codes from the backend */
export type OtpErrorCode =
  | 'VERIFICATION_CODE_INVALID'
  | 'EXPIRED'
  | 'NO_ATTEMPTS_LEFT'
  | 'ALREADY_VIEWED'
  | 'RATE_LIMITED'
  | 'OTP_NOT_FOUND';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Decode the base64-encoded `report` query parameter */
export function decodeReportParam(report: string): OtpReportPayload | null {
  try {
    const json = atob(report);
    const raw = JSON.parse(json);
    return {
      listingKey: raw.listingKey,
      ownerNumber: raw.ownerNumber || raw.owner,
      email: raw.email,
      phone: raw.phone || raw.phoneNumber,
      agentKey: raw.agentKey,
      tenant: raw.tenant,
    };
  } catch {
    return null;
  }
}

/** Mask an email: "john.doe@example.com" → "j*****e@example.com" */
export function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}@${domain}`;
}

/** Mask a phone: "3015551234" → "***-***-1234" */
export function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***-***-${digits.slice(-4)}`;
}
